import React, { useMemo, useState } from 'react';

import { Point2D as Point2DInterface, ProfileLineType } from '@paragon/contract-library';
import { angleEquals, distanceEquals, LineSegment2D, Point2D, Point3D, Vector2D } from '@paragon/geometry-library';
import { notNullish } from '@paragon/utilities';

import { useAppSelector } from '../../components/hooks';
import { ProfileClass } from '../../library/profiles/ProfileClass';
import { ProfileLineClass } from '../../library/profiles/ProfileLineClass';
import { useMousyProfile } from '../../store/mousyInsertionProfileStore';
import { getActiveComponentDesign } from '../../store/selectors/getActiveComponentDesignSelector';
import { getInsertingObjectInfo } from '../../store/selectors/insertionObjectSelectors';
import { getMousyProfileInsertionInformation } from '../../store/selectors/mousyProfileInsertionSelectors';
import { getLabelSizeFromTextMesh, LabelSize } from '../util/LabelVisual';
import { QuickDistanceDimension } from './QuickDistanceDimension';
import { SlopeLabel } from './SlopeLabel';

const DISTANCE_DIMENSION_OFFSET = 10;
const SLOPE_LABEL_OFFSET_PADDING = 3;

const BASE_DIMENSION_PROPS = {
  thickness: 0.2,
  showEndpointMarks: true,
};

function VerticalProfileLineDistanceDimension(props: {
  baseJoint: Point2DInterface;
  endJoint: Point2DInterface;
  profileLineType: ProfileLineType;
}) {
  const { baseJoint, endJoint, profileLineType } = props;

  const profileSegment = LineSegment2D.fromPointObjects(baseJoint, endJoint);
  // Since lines are oriented left to right, we can rotate the profile segment clockwise for top chords and
  // counter-clockwise for bottom chords to get the desired offset direction for the dimension.
  const offsetDirection =
    profileLineType === 'TopChord'
      ? profileSegment.direction.rotateQuarterTurn()
      : profileSegment.direction.rotateReverseQuarterTurn();
  const offsetVector = Vector2D.fromDirectionAndMagnitude(offsetDirection, DISTANCE_DIMENSION_OFFSET);
  const dimensionSegment = profileSegment.translate(offsetVector);

  return (
    <QuickDistanceDimension
      {...BASE_DIMENSION_PROPS}
      start={dimensionSegment.basePoint}
      end={dimensionSegment.endPoint}
      distance={Math.abs(baseJoint.y - endJoint.y)}
    />
  );
}

function HorizontalProfileLineDistanceDimension(props: {
  baseJoint: Point2DInterface;
  endJoint: Point2DInterface;
  dimensionHeight: number;
}) {
  const { baseJoint, endJoint, dimensionHeight } = props;

  return (
    <QuickDistanceDimension
      {...BASE_DIMENSION_PROPS}
      distance={Math.abs(baseJoint.x - endJoint.x)}
      start={{ x: baseJoint.x, y: dimensionHeight }}
      end={{ x: endJoint.x, y: dimensionHeight }}
    />
  );
}

interface SlopedChordLabelProps {
  readonly slopedChord: ProfileLineClass;
}

function SlopedChordLabel({ slopedChord }: SlopedChordLabelProps) {
  const [labelSize, setLabelSize] = useState<LabelSize | null>(null);

  const position = useMemo(() => {
    if (!labelSize) return Point3D.origin;

    const offset = getOffsetToPlaceLabelOnExteriorOfLine(
      slopedChord.segment,
      slopedChord.profileLineType === 'TopChord' ? 'Port' : 'Starboard',
      labelSize,
    );
    const offsetWithPadding = Vector2D.fromDirectionAndMagnitude(
      offset.direction,
      offset.magnitude + SLOPE_LABEL_OFFSET_PADDING,
    );
    return slopedChord.segment.midpoint.translate(offsetWithPadding).to3D();
  }, [labelSize, slopedChord.profileLineType, slopedChord.segment]);

  return (
    <SlopeLabel
      degrees={slopedChord.segment.direction.angle}
      position={position}
      // Hide the label until we're able to position it using its size.
      visible={!!labelSize}
      onTextSync={(text) => {
        setLabelSize(getLabelSizeFromTextMesh(text));
      }}
    />
  );
}

export function MousyProfileInsertionDimensions() {
  const mousyProfile = useMousyProfile();

  const componentDesign = useAppSelector(getActiveComponentDesign);
  const insertingObjectInfo = useAppSelector(getInsertingObjectInfo);
  const mousyInsertion = useAppSelector(getMousyProfileInsertionInformation);

  const dimensionSetExists = useAppSelector((state) =>
    componentDesign?.dimensionSetGuid ? !!state.dimensions.sets[componentDesign.dimensionSetGuid] : false,
  );

  if (
    insertingObjectInfo?.type !== 'componentDesign' ||
    insertingObjectInfo.designSettings.definitionType !== 'Profile'
  ) {
    return null;
  }

  const profile = (mousyInsertion && mousyProfile) ?? insertingObjectInfo.designSettings.profile;
  const profileTrussSettings = { ...insertingObjectInfo.designSettings, profile };

  const profileClassResult = ProfileClass.create(profileTrussSettings);
  if (profileClassResult.type === 'Err') return null;
  const profileClass = profileClassResult.ok;

  const lineKeys = new Map<string, string>([
    ...profileClass.topChords.map((chord, index) => [chord.guid, `top-${index}`] as const),
    ...profileClass.bottomChords.map((chord, index) => [chord.guid, `bottom-${index}`] as const),
    ...(profileClass.leftSeatCut ? [[profileClass.leftSeatCut.guid, 'left-seat-cut'] as const] : []),
    ...(profileClass.rightSeatCut ? [[profileClass.rightSeatCut.guid, 'right-seat-cut'] as const] : []),
  ]);

  const slopeDimensions = profileClass.topChords
    .concat(profileClass.bottomChords)
    .filter((chord) => !angleEquals(chord.segment.direction.angle % 90, 0))
    .map((slopedChord) => (
      <SlopedChordLabel key={`${lineKeys.get(slopedChord.guid)}-slope`} slopedChord={slopedChord} />
    ));

  // If a real dimension set exists, we don't want to show duplicate distance dimensions.
  const topLinesToDimension = dimensionSetExists ? [] : profileClass.topChords;
  const bottomLinesToDimension = dimensionSetExists
    ? []
    : [profileClass.leftSeatCut, ...profileClass.bottomChords, profileClass.rightSeatCut].filter(notNullish);

  const topHorizontalDimensions = [];
  const bottomHorizontalDimensions = [];
  const verticalDimensions = [];

  function profileLineToDimensionProps(profileLine: ProfileLineClass) {
    return {
      key: `${lineKeys.get(profileLine.guid)}-distance`,
      baseJoint: profileLine.basePoint,
      endJoint: profileLine.endPoint,
      profileLineType: profileLine.profileLineType,
    };
  }

  for (const topLine of topLinesToDimension) {
    if (distanceEquals(topLine.basePoint.x, topLine.endPoint.x)) {
      verticalDimensions.push(profileLineToDimensionProps(topLine));
    } else {
      topHorizontalDimensions.push(profileLineToDimensionProps(topLine));
    }
  }

  for (const bottomLine of bottomLinesToDimension) {
    if (distanceEquals(bottomLine.basePoint.x, bottomLine.endPoint.x)) {
      verticalDimensions.push(profileLineToDimensionProps(bottomLine));
    } else {
      bottomHorizontalDimensions.push(profileLineToDimensionProps(bottomLine));
    }
  }

  const potentialHighestDimensionPoints = topHorizontalDimensions
    .concat(verticalDimensions)
    .flatMap(({ baseJoint, endJoint }) => [baseJoint.y, endJoint.y]);
  const highestDimensionPoint = Math.max(...potentialHighestDimensionPoints) + DISTANCE_DIMENSION_OFFSET;

  const potentialLowestDimensionPoints = bottomHorizontalDimensions
    .concat(verticalDimensions)
    .flatMap(({ baseJoint, endJoint }) => [baseJoint.y, endJoint.y]);
  const lowestDimensionPoint = Math.min(...potentialLowestDimensionPoints) - DISTANCE_DIMENSION_OFFSET;

  return (
    <group>
      {slopeDimensions}

      {topHorizontalDimensions.map(({ key, ...props }) => (
        <HorizontalProfileLineDistanceDimension {...props} key={key} dimensionHeight={highestDimensionPoint} />
      ))}

      {bottomHorizontalDimensions.map(({ key, ...props }) => (
        <HorizontalProfileLineDistanceDimension {...props} key={key} dimensionHeight={lowestDimensionPoint} />
      ))}

      {verticalDimensions.map(({ key, ...props }) => (
        <VerticalProfileLineDistanceDimension {...props} key={key} />
      ))}
    </group>
  );
}

function getOffsetToPlaceLabelOnExteriorOfLine(
  segment: LineSegment2D,
  exteriorSide: 'Port' | 'Starboard',
  labelSize: LabelSize,
) {
  const exteriorDirection =
    exteriorSide === 'Port' ? segment.direction.rotateQuarterTurn() : segment.direction.rotateReverseQuarterTurn();

  // This will be the point that is furthest on the interior side of the line.
  // Further down we project this point onto the line to figure out how much of an offset we need to get the entire box
  // to be on the exterior side of the line.
  let interiorBoundingBoxPoint: Point2D;

  const exteriorAngle = exteriorDirection.angle;
  if (exteriorAngle < -180 || exteriorAngle > 180)
    throw new Error('Expected angle to be between -180 and 180 degrees.');

  const { width, height } = labelSize;
  const minX = segment.midpoint.x - width / 2;
  const minY = segment.midpoint.y - height / 2;
  const maxX = segment.midpoint.x + width / 2;
  const maxY = segment.midpoint.y + width / 2;
  if (exteriorAngle <= -90) interiorBoundingBoxPoint = new Point2D(maxX, maxY);
  else if (exteriorAngle <= 0) interiorBoundingBoxPoint = new Point2D(minX, maxY);
  else if (exteriorAngle <= 90) interiorBoundingBoxPoint = new Point2D(minX, minY);
  else if (exteriorAngle <= 180) interiorBoundingBoxPoint = new Point2D(maxX, minY);
  else throw new Error('Angle out of range.');

  const projectedPoint = interiorBoundingBoxPoint.projectOntoLine(segment.toLine());
  return Vector2D.fromPointAndPoint(interiorBoundingBoxPoint, projectedPoint);
}
